<template>
  <v-dialog :value="value" persistent max-width="960" content-class="separar-cuenta" @input="$emit('input', $event)">
    <v-card class="separar__tarjeta">
      <EncabezadoModal titulo="Separar cuenta" subtitulo="Cada cliente paga lo suyo" icono="mdi-call-split" @cerrar="cerrar()" />

      <div class="separar__estado">
        <div class="separar__saldo">
          <span class="separar__etiqueta">Falta por pagar</span>
          <strong>{{ $filters.currency(order.getBalance(), 0) }}</strong>
        </div>
        <div class="separar__progreso">
          <div class="separar__barra"><i :style="{ width: progreso + '%' }"></i></div>
          <div class="separar__pagadas">
            <span v-if="!pagosCuentas.length" class="separar__etiqueta">Aún no hay cuentas pagadas</span>
            <v-chip v-for="c in pagosCuentas" :key="c.id" small label color="success" text-color="white">
              <v-icon x-small left>mdi-check-circle</v-icon>Cuenta {{ c.n }} · {{ c.metodo }} · {{ $filters.currency(c.valor, 0) }}
            </v-chip>
          </div>
        </div>
      </div>

      <div class="separar__cuerpo">
        <div class="separar__listas">
          <section class="separar__lista">
            <header>
              <span><v-icon small color="primary" class="mr-1">mdi-format-list-bulleted</v-icon>Por pagar</span>
              <v-btn small text color="primary" :disabled="!hayPendientes" @click="pasarTodo()">Pasar todo <v-icon small right>mdi-arrow-right</v-icon></v-btn>
            </header>
            <div class="separar__scroll">
              <p v-if="!hayPendientes" class="separar__vacio"><v-icon color="success" large>mdi-check-circle-outline</v-icon>Todos los productos ya están en una cuenta</p>
              <button v-for="l in lineasPendientes" :key="'p' + l.i" type="button" class="separar__linea" @click="pasar(l)">
                <span class="separar__texto">
                  <span class="separar__nombre">{{ l.nombre }}</span>
                  <span class="separar__cant">{{ l.pendientes }} de {{ l.cantidad }} por pagar</span>
                </span>
                <span class="separar__valor">{{ $filters.currency(l.valorPendiente, 0) }}</span>
                <span class="separar__boton"><v-icon small color="white">mdi-plus</v-icon></span>
              </button>
            </div>
          </section>

          <section class="separar__lista separar__lista--cuenta">
            <header>
              <span><v-icon small color="primary" class="mr-1">mdi-account-outline</v-icon>Cuenta {{ numeroCuenta }}</span>
              <v-btn small text color="primary" :disabled="!hayEnCuenta" @click="devolverTodo()"><v-icon small left>mdi-arrow-left</v-icon>Devolver todo</v-btn>
            </header>
            <div class="separar__scroll">
              <p v-if="!hayEnCuenta" class="separar__vacio"><v-icon large color="grey lighten-1">mdi-gesture-tap</v-icon>Toca un producto para pasarlo a esta cuenta</p>
              <button v-for="l in lineasEnCuenta" :key="'c' + l.i" type="button" class="separar__linea separar__linea--cuenta" @click="devolver(l)">
                <span class="separar__boton separar__boton--quitar"><v-icon small color="primary">mdi-minus</v-icon></span>
                <span class="separar__texto">
                  <span class="separar__nombre">{{ l.nombre }}</span>
                  <span class="separar__cant">{{ l.enCuenta }} {{ l.enCuenta == 1 ? "unidad" : "unidades" }}</span>
                </span>
                <span class="separar__valor">{{ $filters.currency(l.valorEnCuenta, 0) }}</span>
              </button>
            </div>
            <footer class="separar__total">
              <span>Total de la cuenta {{ numeroCuenta }}</span>
              <strong>{{ $filters.currency(valorCuenta, 0) }}</strong>
            </footer>
          </section>
        </div>

        <div class="separar__cobro">
          <span class="separar__etiqueta">¿Cómo paga la cuenta {{ numeroCuenta }}?</span>
          <div class="separar__metodos">
            <button v-for="m in principales" :key="m.id" type="button" class="separar__metodo" :class="{ 'separar__metodo--activo': metodo && metodo.id === m.id }" @click="elegir(m)">
              <v-icon :color="metodo && metodo.id === m.id ? 'white' : 'primary'">{{ iconoMetodo(m) }}</v-icon>{{ m.name }}
            </button>
            <v-menu v-if="otros.length" top offset-y max-height="260">
              <template v-slot:activator="{ on }">
                <button v-on="on" type="button" class="separar__metodo" :class="{ 'separar__metodo--activo': metodoEsOtro }">
                  <v-icon :color="metodoEsOtro ? 'white' : 'primary'">mdi-dots-horizontal</v-icon>{{ metodoEsOtro ? metodo.name : "Otros" }}
                </button>
              </template>
              <v-list dense>
                <v-list-item v-for="m in otros" :key="m.id" @click="elegir(m)"><v-list-item-title>{{ m.name }}</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </div>

          <div v-if="metodo && metodo.manage_change && hayEnCuenta" class="separar__efectivo">
            <span class="separar__etiqueta">Recibe</span>
            <v-chip v-for="v in sugeridos" :key="v" label :outlined="Number(recibe) !== v" color="primary" @click="recibe = v">{{ v === valorCuenta ? "Justo" : $filters.currency(v, 0) }}</v-chip>
            <ng-number-keyboard v-model="recibe" label="Otro valor" outlined :filled="false" dense hide-details class="separar__recibe"></ng-number-keyboard>
            <span v-if="cambio > 0" class="separar__cambio">Cambio <strong>{{ $filters.currency(cambio, 0) }}</strong></span>
            <span v-if="faltaRecibido > 0" class="separar__falta">Faltan <strong>{{ $filters.currency(faltaRecibido, 0) }}</strong></span>
          </div>
          <ng-number-keyboard v-if="metodo && metodo.need_reference" v-model="referencia" label="Referencia" rules="required" show-required="true" outlined :filled="false" dense hide-details class="mt-2 separar__referencia"></ng-number-keyboard>
          <small class="separar__nota">La tarjeta de regalo se cobra desde la pestaña normal de pago.</small>
        </div>
      </div>

      <v-card-actions class="separar__pie">
        <v-btn outlined large color="primary" @click="cerrar()">Cerrar</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="success" depressed x-large class="separar__cobrar" :disabled="!puedeCobrar" @click="cobrar()">
          <v-icon left>mdi-cash-check</v-icon>Cobrar cuenta {{ numeroCuenta }} · {{ $filters.currency(valorCuenta, 0) }}<template v-if="metodo"> en {{ metodo.name }}</template>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ItemLine from "@/plugins/pos/item-line";

/**
 * Separar cuenta: cada cliente paga lo suyo. Los productos pasan de «Por pagar» a «Esta cuenta» y al cobrar se crea UN pago en la
 * lista de pagos de la orden (con la lista de lo que cubre). Lo cubierto sale de «Por pagar»; si se borra ese pago, vuelve.
 * No cambia la orden ni el servidor: sigue siendo una venta con varios pagos.
 */
export default {
  name: "SepararCuenta",
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, required: true },
    paymentMethods: { type: [Object, Array], default: () => ({}) },
  },
  data: () => ({
    seleccion: {}, // índice de línea -> unidades pasadas a esta cuenta
    metodo: null,
    referencia: "",
    recibe: "",
  }),
  computed: {
    metodos() {
      const todos = Array.isArray(this.paymentMethods) ? this.paymentMethods : [].concat(...Object.values(this.paymentMethods || {}));
      return todos.filter((m) => m && m.code !== "tarjeta_regalo");
    },
    // Cada línea de la orden con lo ya cobrado en cuentas anteriores y lo que está en esta cuenta.
    lineas() {
      const pagadas = this.unidadesPagadas();
      return this.order.getItems(true).map((item, i) => {
        const cantidad = parseFloat(item.quantity) || 0;
        const total = Math.round(ItemLine.getTotal(item) * this.factorPuntos);
        const firma = this.firma(item);
        const ya = pagadas[i + "|" + firma] || 0;
        const sel = this.seleccion[i] || 0;
        const pendientes = Math.max(cantidad - ya - sel, 0);
        const nombre = (item.product && item.product.name ? item.product.name : "Producto") + (item.product_variant && item.product_variant.name ? " · " + item.product_variant.name : "");
        return {
          i, item, cantidad, total, firma, ya, nombre,
          pendientes,
          enCuenta: sel,
          valorPendiente: total - this.acumulado(total, cantidad, ya + sel),
          valorEnCuenta: this.acumulado(total, cantidad, ya + sel) - this.acumulado(total, cantidad, ya),
        };
      });
    },
    // El descuento por puntos se reparte entre los productos en proporción a su valor: antes lo absorbía toda la última
    // cuenta, y las primeras pagaban precio lleno.
    factorPuntos() {
      const suma = this.order.getItems(true).reduce((t, item) => t + ItemLine.getTotal(item), 0);
      const descuento = parseFloat(this.order.getDiscountPoints()) || 0;
      return suma > 0 && descuento > 0 ? Math.max(0, (suma - descuento) / suma) : 1;
    },
    lineasPendientes() {
      return this.lineas.filter((l) => l.pendientes > 0);
    },
    lineasEnCuenta() {
      return this.lineas.filter((l) => l.enCuenta > 0);
    },
    hayPendientes() {
      return this.lineasPendientes.length > 0;
    },
    hayEnCuenta() {
      return this.lineasEnCuenta.length > 0;
    },
    numeroCuenta() {
      return this.cuentasPagadas + 1;
    },
    // Las cuentas ya cobradas, en el orden en que se cobraron.
    pagosCuentas() {
      return this.order
        .getPayments()
        .filter((p) => p.lineas && p.lineas.length)
        .map((p, k) => ({ id: p._id, n: k + 1, metodo: p.payment_method ? p.payment_method.name : "", valor: p.value }));
    },
    progreso() {
      const total = this.order.getPriceTotalWithAdds();
      return total > 0 ? Math.min(100, Math.round((this.order.getTotalPayments() / total) * 100)) : 0;
    },
    // Los cuatro medios más usados quedan a la vista; el resto, en «Otros».
    principales() {
      return this.metodos.slice(0, 4);
    },
    otros() {
      return this.metodos.slice(4);
    },
    metodoEsOtro() {
      return !!this.metodo && this.otros.some((m) => m.id === this.metodo.id);
    },
    // Billetes que el cliente probablemente entrega: lo justo y el siguiente billete redondo.
    sugeridos() {
      const v = this.valorCuenta;
      if (!(v > 0)) {
        return [];
      }
      const arriba = (b) => Math.ceil(v / b) * b;
      return [...new Set([v, arriba(10000), arriba(50000), arriba(100000)])].filter((x) => x >= v).slice(0, 4);
    },
    cuentasPagadas() {
      return this.order.getPayments().filter((p) => p.lineas && p.lineas.length).length;
    },
    // Lo que vale esta cuenta. Si con ella se cubren todos los productos, lleva también lo que no es de un producto
    // (propina, domicilio, puntos) y cualquier saldo que quede.
    valorCuenta() {
      const balance = this.order.getBalance();
      if (!this.hayEnCuenta || !(balance > 0)) {
        return 0;
      }
      if (!this.hayPendientes) {
        return balance;
      }
      const suma = this.lineasEnCuenta.reduce((t, l) => t + l.valorEnCuenta, 0);
      return Math.min(suma, balance);
    },
    // En efectivo, lo recibido tiene que alcanzar: antes «Recibe 20.000» en una cuenta de 35.000 registraba 35.000 sin
    // avisar. Vacío es el valor justo.
    faltaRecibido() {
      const r = parseFloat(this.recibe);
      return this.metodo && this.metodo.manage_change && String(this.recibe || "").trim() !== "" && r < this.valorCuenta ? this.valorCuenta - r : 0;
    },
    cambio() {
      const r = parseFloat(this.recibe);
      return this.metodo && this.metodo.manage_change && r > this.valorCuenta ? r - this.valorCuenta : 0;
    },
    puedeCobrar() {
      if (!this.hayEnCuenta || !this.metodo || !(this.valorCuenta > 0)) {
        return false;
      }
      if (this.metodo.need_reference && !String(this.referencia || "").trim()) {
        return false;
      }
      return !(this.faltaRecibido > 0);
    },
  },
  watch: {
    value(abierto) {
      if (abierto) {
        this.reiniciar();
      }
    },
  },
  methods: {
    iconoMetodo(m) {
      const n = String(m.code || "") + " " + String(m.name || "");
      if (/efectivo|cash/i.test(n)) return "mdi-cash";
      if (/d[eé]bito|cr[eé]dito|tarjeta|card/i.test(n)) return "mdi-credit-card-outline";
      if (/transfer/i.test(n)) return "mdi-bank-transfer";
      return "mdi-wallet-outline";
    },
    firma(item) {
      return (item.product ? item.product.id : "") + "|" + item.quantity + "|" + item.total_amount;
    },
    // Valor de las primeras `unidades` de una línea: la última completa el total exacto, sin perder centavos por redondeo.
    acumulado(total, cantidad, unidades) {
      if (unidades <= 0 || cantidad <= 0) {
        return 0;
      }
      return unidades >= cantidad ? total : Math.round((total * unidades) / cantidad);
    },
    // Unidades ya cobradas por línea, leídas de los pagos de la orden (solo cuentan si la línea no cambió).
    unidadesPagadas() {
      const res = {};
      for (const p of this.order.getPayments()) {
        for (const l of p.lineas || []) {
          const k = l.i + "|" + l.firma;
          res[k] = (res[k] || 0) + l.unidades;
        }
      }
      return res;
    },
    // Un toque pasa una unidad; si la cantidad es fraccionaria (peso) pasa lo que quede.
    pasos(l, unidades) {
      return Number.isInteger(l.cantidad) ? Math.min(1, unidades) : unidades;
    },
    pasar(l) {
      this.$set(this.seleccion, l.i, (this.seleccion[l.i] || 0) + this.pasos(l, l.pendientes));
    },
    devolver(l) {
      const resto = l.enCuenta - this.pasos(l, l.enCuenta);
      if (resto <= 0) {
        this.$delete(this.seleccion, l.i);
      } else {
        this.$set(this.seleccion, l.i, resto);
      }
    },
    pasarTodo() {
      this.lineasPendientes.forEach((l) => this.$set(this.seleccion, l.i, (this.seleccion[l.i] || 0) + l.pendientes));
    },
    devolverTodo() {
      this.seleccion = {};
    },
    elegir(m) {
      this.metodo = m;
      this.referencia = "";
      this.recibe = "";
    },
    reiniciar() {
      this.seleccion = {};
      this.referencia = "";
      this.recibe = "";
      if (!this.metodo && this.metodos.length) {
        this.metodo = this.metodos[0];
      }
    },
    cobrar() {
      if (!this.puedeCobrar) {
        return;
      }
      const valor = this.valorCuenta;
      const turn = this.$store.getters.turn;
      const lineas = this.lineasEnCuenta.map((l) => ({ i: l.i, firma: l.firma, unidades: l.enCuenta }));
      this.$pos.addPayment(this.order, {
        _id: new Date().getTime(),
        payment_method: this.metodo,
        date: this.$filters.datetime(new Date()),
        value: valor,
        acc_income_id: null,
        reference: this.metodo.need_reference ? this.referencia : null,
        bill_cash_register_id: this.order.getBillCashRegisterId(),
        bill_cash_register_turn_id: turn ? turn.id : null,
        change: this.cambio,
        lineas,
      });
      this.seleccion = {};
      this.referencia = "";
      this.recibe = "";
      this.$emit("cobrada");
      if (!(this.order.getBalance() > 0)) {
        this.cerrar();
      }
    },
    cerrar() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.separar__falta {
  color: #c62828;
  font-weight: 500;
}
.separar__tarjeta {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
  overflow: hidden;
}
.separar__etiqueta {
  display: block;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5f6368;
}
.separar__estado {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  background: #f5f8fb;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.separar__saldo strong {
  display: block;
  font-size: 26px;
  line-height: 1.1;
  color: #c62828;
}
.separar__progreso {
  flex: 1;
  min-width: 0;
}
.separar__barra {
  height: 8px;
  border-radius: 4px;
  background: #dfe6ec;
  overflow: hidden;
}
.separar__barra i {
  display: block;
  height: 100%;
  background: #4caf50;
  transition: width 0.3s;
}
.separar__pagadas {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
  min-height: 24px;
}
.separar__cuerpo {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px 0;
}
.separar__listas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.separar__lista {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.separar__lista--cuenta {
  border: 2px solid var(--pos-azul, #24a9e1);
  background: #f4fbfe;
}
.separar__lista header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 12px;
  font-weight: 700;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.separar__scroll {
  flex: 1;
  height: 176px;
  overflow-y: auto;
}
.separar__vacio {
  margin: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  text-align: center;
  color: #5f6368;
  font-size: 14px;
}
.separar__linea {
  width: 100%;
  min-height: 56px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 6px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.separar__linea:active {
  background: rgba(36, 169, 225, 0.14);
}
.separar__linea--cuenta {
  padding-left: 10px;
}
.separar__texto {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.separar__nombre {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
}
.separar__cant {
  font-size: 12px;
  color: #5f6368;
}
.separar__valor {
  font-weight: 700;
  white-space: nowrap;
  color: var(--pos-azul-marino, #0b1f33);
}
.separar__boton {
  flex: none;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--pos-azul, #24a9e1);
}
.separar__boton--quitar {
  background: #fff;
  border: 2px solid var(--pos-azul, #24a9e1);
}
.separar__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--pos-azul-marino, #0b1f33);
  color: #fff;
}
.separar__total strong {
  font-size: 20px;
}
.separar__cobro {
  padding: 12px 0 10px;
}
.separar__metodos {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: 6px;
}
.separar__metodo {
  min-height: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 6px;
  border: 1.5px solid var(--pos-azul, #24a9e1);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  color: var(--pos-azul-marino, #0b1f33);
}
.separar__metodo--activo {
  background: var(--pos-azul, #24a9e1);
  color: #fff;
}
.separar__efectivo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.separar__recibe {
  max-width: 170px;
  flex: none;
}
.separar__cambio {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  margin-left: auto;
  padding: 4px 16px;
  border-radius: 10px;
  background: #e8f5e9;
  border: 2px solid #4caf50;
  color: #1b5e20;
  font-size: 16px;
  font-weight: 600;
}
.separar__cambio strong {
  font-size: 28px;
  line-height: 1.1;
}
.separar__referencia {
  max-width: 320px;
}
.separar__nota {
  display: block;
  margin-top: 6px;
  color: #5f6368;
}
.separar__pie {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 10px 16px;
}
.separar__cobrar {
  letter-spacing: 0;
  font-weight: 700;
}
@media (max-width: 700px) {
  .separar__listas {
    grid-template-columns: 1fr;
  }
  .separar__metodos {
    grid-template-columns: repeat(2, 1fr);
  }
  .separar__estado {
    flex-direction: column;
    align-items: stretch;
  }
}
@media (max-height: 800px) {
  .separar__scroll {
    height: 150px;
  }
}
</style>
